import { Button } from "components/Button";
import { useCampaignsStore } from "screens/Campaigns/store";
import { CampaignAddForm } from "../../CampaignAddForm";
import { SquareLoader } from "screens/Main/components/Loader/SquareLoader";
import InfiniteScroll from "react-infinite-scroll-component";
import { motion } from "framer-motion";
import { campaignsApi } from "screens/Campaigns/service";
import { useEffect, useState } from "react";
import { CampaignCard } from "../../Cards/CampaignCard";

export const CampaignsPanel = () => {
  const {
    isOpenAddCampaignForm,
    setIsOpenAddCampaignForm,
    hasMoreCampaigns,
    campaignsPage,
    isLoadMoreLoading,
    setIsLoadMoreLoading,
    setCampaignsPage,
    totalCampaignsPages,
    campaigns,
    setCampaigns
  } = useCampaignsStore();
  const [isLoading, setIsLoading] = useState(false);

  const getAllCampaignsHandler = async () => {
    setIsLoading(true);
    try {
      const pageToLoad = 1;
      const campaigns = await campaignsApi.getAllCampaigns(pageToLoad);

      if (campaigns) {
        setCampaigns(campaigns);
        setCampaignsPage(pageToLoad);
      }
    } catch (error) {
      console.error("Error loading campaigns data", error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCampaignHandler = async (id: string) => {
    setIsLoading(true);
    await campaignsApi.deleteCampaign(id);
    await getAllCampaignsHandler();
    setIsLoading(false);
  };

  useEffect(() => {
    getAllCampaignsHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = async () => {
    if (!hasMoreCampaigns) return;
    if (totalCampaignsPages < campaignsPage + 1) return;

    setIsLoadMoreLoading(true);

    const nextPage = campaignsPage + 1;

    try {
      const nextData = await campaignsApi.getAllCampaigns(nextPage);

      const isThereMore = nextPage < totalCampaignsPages;
      setCampaignsPage(isThereMore ? nextPage : campaignsPage);

      if (nextData) {
        setCampaigns([...campaigns, ...nextData]);
      }
    } catch (error) {
      console.error("Error loading campaigns data", error);
    } finally {
      setIsLoadMoreLoading(false);
    }
  };

  return (
    <>
      <Button
        extraClass="flex items-center justify-center justify-self-center mb-[60px] min-w-[192px]"
        onClick={() => setIsOpenAddCampaignForm(true)}
      >
        <span className="mr-[10px] text-[18px]">+</span> Add new campaign
      </Button>
      <div className="w-full">
        <p className="font-[700] mb-[24px]">
          All campaigns ({campaigns.length})
        </p>
        {isLoading ? (
          <div className="flex w-full  my-[20px] justify-center  items-center">
            <SquareLoader size={32} />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={campaigns ? campaigns.length : 0}
            next={() => loadMore()}
            hasMore={hasMoreCampaigns}
            loader={null}
            endMessage={null}
          >
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7 w-full mb-16">
              {campaigns && campaigns?.length > 0 ? (
                campaigns.map((campaign, i) => (
                  <motion.article
                    initial={{
                      y: 50,
                      opacity: 0
                    }}
                    whileInView={{
                      y: 0,
                      opacity: 1
                    }}
                    transition={{
                      duration: 0.1,
                      delay: 0.1 * (i % 3)
                    }}
                    viewport={{
                      once: true,
                      margin: "-20% 0%"
                    }}
                    key={campaign.id || `${campaign.title}-${i}`}
                    className="relative cursor-pointer flex flex-col gap-[8px] min-h-[251px] py-[22px] pl-[19px] pr-[24px] bg-[#171717]"
                  >
                    <CampaignCard
                      campaign={campaign}
                      deleteCampaign={deleteCampaignHandler}
                    />
                  </motion.article>
                ))
              ) : (
                <p className="W-full flex items-center justify-center mt-[50px]">
                  No campaigns available
                </p>
              )}
            </section>
          </InfiniteScroll>
        )}
        {hasMoreCampaigns && !isLoading && (
          <div className="flex w-full justify-center">
            <Button
              extraClass="flex items-center justify-center mb-[60px] min-w-[192px]"
              onClick={loadMore}
            >
              {isLoadMoreLoading ? (
                <div className="flex w-full justify-center  items-center">
                  <SquareLoader size={32} />
                </div>
              ) : (
                <span>Load more...</span>
              )}
            </Button>
          </div>
        )}
      </div>
      <CampaignAddForm
        isOpen={isOpenAddCampaignForm}
        onClose={() => setIsOpenAddCampaignForm(false)}
        getAllCampaignsHandler={getAllCampaignsHandler}
      />
    </>
  );
};
